import {differenceInMinutes, parse, format} from 'date-fns'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const getInitials = (name = '') => {
    const parts = name.trim().split(' ').filter(Boolean)

    if (parts.length === 0) return ''
    if (parts.length === 1) return parts[0][0].toUpperCase()

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export const getColorFromName = (name = '') => {
    const avatarColors = ['#B71C1C', '#880E4F', '#4A148C', '#1A237E', '#0D47A1', '#004D40', '#1B5E20', '#E65100', '#3E2723', '#263238']

    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % avatarColors.length

    return avatarColors[index]
}

export const phoneRegex = /^[+]?[\d\s\-()]{10,20}$/

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const toSnakeCase = str => {
    return str
        .replace(/\s+/g, '_')
        .replace(/[^\w_]/g, '')
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .toLowerCase()
}

export const capitalizeFirst = str => {
    if (typeof str !== 'string' || !str) {
        return ''
    }
    return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

export const parseError = error => {
    if (error?.response?.data?.error?.message) {
        const {
            response: {
                data: {
                    error: {code, message},
                },
            },
        } = error
        return {title: code, description: message}
    } else if (error?.response?.data?.error) {
        const {
            response: {
                data: {error: errorMessage},
            },
        } = error
        return {title: error?.message, description: errorMessage}
    } else if (error?.message) {
        const {code, message} = error
        return {title: code, description: message}
    } else {
        return {title: null, description: null}
    }
}

export const usStates = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
]

export const thousantSeparator = value => {
    if (typeof value !== 'string') value = String(value)

    const [integerPart, decimalPart] = value.replace(/,/g, '').split('.')

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger
}

export const ignoreTimezone = dateString => {
    if (!dateString) {
        return null
    }

    return dateString.substring(0, 10) + 'T00:00:00'
}

export const formatTimeDiffShort = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(':').map(Number)
    const [endHour, endMinute] = endTime.split(':').map(Number)
    const start = new Date(1970, 0, 1, startHour, startMinute)
    const end = new Date(1970, 0, 1, endHour, endMinute)
    const diffMinutes = differenceInMinutes(end, start)
    const hours = Math.floor(diffMinutes / 60)
    const minutes = diffMinutes % 60
    if (hours > 0 && minutes > 0) {
        return `${hours}hr${minutes}mn`
    } else if (hours > 0) {
        return `${hours}hr`
    } else {
        return `${minutes}mn`
    }
}

export const formatTime = timeString => {
    if (!timeString) {
        return null
    }
    return format(parse(timeString?.slice(0, 5), 'HH:mm', new Date()), 'h:mm a')
}

// Categories for the caregiver Backgrounds documents. The values must match
// backgroundDocumentCategories in backend/internal/caregiver/background_service.go.
export const caregiverDocumentCategories = {
    governmentId: [
        {value: 'drivers_license', label: "Driver's License"},
        {value: 'state_id', label: 'State ID'},
        {value: 'passport', label: 'Passport'},
        {value: 'social_security_card', label: 'Social Security Card'},
        {value: 'other', label: 'Other'},
    ],
    criminalRecord: [
        {value: 'background_check', label: 'Background Check'},
        {value: 'fingerprint_clearance', label: 'Fingerprint Clearance'},
        {value: 'sex_offender_registry', label: 'Sex Offender Registry Check'},
        {value: 'other', label: 'Other'},
    ],
    licenses: [
        {value: 'cna', label: 'CNA Certification'},
        {value: 'hha', label: 'HHA Certification'},
        {value: 'rn_license', label: 'RN License'},
        {value: 'lpn_license', label: 'LPN License'},
        {value: 'cpr_first_aid', label: 'CPR / First Aid'},
        {value: 'other', label: 'Other'},
    ],
}

// The office types a wall-clock time ("11:00" on 2026-06-03) that means 11:00 in the
// schedule's own zone. Returns the RFC3339 UTC instant for it, e.g.
// "2026-06-03T16:00:00Z" for America/Chicago. Without a zone the browser's is used.
export const zonedWallTimeToUtcIso = (date, time, zone) => {
    const wall = `${date} ${time.slice(0, 5)}`
    const instant = zone ? dayjs.tz(wall, zone) : dayjs(wall)
    return instant.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

// The inverse of zonedWallTimeToUtcIso: the wall-clock "HH:mm" an RFC3339 instant reads in
// the schedule's zone (the browser's when there is none). Null when there is no instant.
export const utcIsoToZonedWallTime = (instant, zone) => {
    if (!instant) return null
    const parsed = dayjs(instant)
    return (zone ? parsed.tz(zone) : parsed).format('HH:mm')
}
